import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import PriceFormComponent from "components/shared/FormComponent/PriceFormComponent";
import { StyledMenuItem, StyledSelect } from "components/shared/StyledSelect";
import type { IChannel } from "containers/Channels/types";
import type { PressChannelSlot } from "helpers/pressCalculationBase";
import { formatNumber } from "helpers/utils";
import { usePermissions } from "hooks/usePermissions";
import type { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

import type { TArticleFormData } from "..";
import type { APaymentMethodOption } from "../utils";

interface IPaymentStep {
    stepTitle: string;
    stepSubtitle: string;
    indexStep: number;
    useFormData: TArticleFormData;
    calculationMethods: { usesFolio: boolean; usesBothCalculationMethods: boolean; usesMinimumGuaranteed: boolean };
    setUsesFolio: Dispatch<SetStateAction<boolean>>;
    paymentMethod: {
        chosenPaymentMethod: IChannel | undefined;
        setChosenPaymentMethod: React.Dispatch<React.SetStateAction<IChannel | undefined>>;
    };
    mode: "new" | "edit";
    /**
     * Formatted press/audio channels
     */
    cleanedChannels: IChannel[];
}

const PaymentStep = ({
    stepSubtitle,
    stepTitle,
    indexStep,
    useFormData,
    calculationMethods,
    setUsesFolio,
    paymentMethod,
    mode,
    cleanedChannels,
}: IPaymentStep) => {
    const [t] = useTranslation();
    const { control, errors, watch } = useFormData;
    const { usesFolio, usesBothCalculationMethods, usesMinimumGuaranteed } = calculationMethods;
    const { chosenPaymentMethod, setChosenPaymentMethod } = paymentMethod;

    const folioCount = isNaN(+(watch("folioCount") ?? 0)) ? 0 : +(watch("folioCount") ?? 0);
    const localFolioPrice = +(watch("folioPrice") ?? 0);

    const { hasPermissions } = usePermissions();

    const handlePaymentMethod = (pressSlot: PressChannelSlot) => {
        const chosenChannel = cleanedChannels.find((ch) => ch.pressSlot === pressSlot);
        if (!chosenChannel) return;
        setChosenPaymentMethod(chosenChannel);
        setUsesFolio(pressSlot === "folio");
    };

    const paymentMethodOptions: APaymentMethodOption[] = [
        { pressSlot: "folio", label: t("NewChannel.Folio") },
        { pressSlot: "amount", label: t("NewChannel.Package") },
        {
            pressSlot: "minimum_guaranteed",
            label: t("NewChannel.MinimumGuarantee"),
            show: usesMinimumGuaranteed,
        },
    ];

    return (
        <>
            <h3 className="form-section-title first">
                {" "}
                {indexStep + 1}. {stepTitle}
            </h3>
            {mode === "new" && <span className="form-section-description">{stepSubtitle}</span>}
            <div className="form-fields">
                {usesBothCalculationMethods && (
                    <div className="article-payment-selector">
                        <label className="form-component-label">{t("NewArticle.PaymentSelection")}</label>
                        <StyledSelect
                            size="small"
                            value={chosenPaymentMethod?.pressSlot ?? ""}
                            onChange={(e) => handlePaymentMethod(e.target.value as PressChannelSlot)}
                            disabled={!hasPermissions(["article:new"])}
                        >
                            {paymentMethodOptions.map((opt) => {
                                const { show = true } = opt;
                                if (!show) return null;
                                return (
                                    <StyledMenuItem value={opt.pressSlot} key={`payment-method-${opt.pressSlot}`}>
                                        {opt.label}
                                    </StyledMenuItem>
                                );
                            })}
                        </StyledSelect>
                    </div>
                )}
                {usesFolio || (usesBothCalculationMethods && usesFolio) ? (
                    <div>
                        <div className="folio-price-container">
                            <NumberFormComponent
                                label={t("NewArticle.FolioCount")}
                                placeholder={""}
                                control={control}
                                controlName="folioCount"
                                float
                                error={errors.folioCount}
                            />
                            <PriceFormComponent
                                label={t("NewArticle.FolioPrice")}
                                placeholder={""}
                                control={control}
                                controlName="folioPrice"
                                error={errors.folioPrice}
                            />
                        </div>
                        <div className="folio-total-price">
                            {t("NewArticle.TotalPrice", {
                                total: formatNumber(folioCount * localFolioPrice, {
                                    isCurrency: true,
                                    always2Digit: true,
                                }),
                                folioCount: folioCount,
                                folioPrice: formatNumber(localFolioPrice, {
                                    isCurrency: true,
                                    always2Digit: true,
                                }),
                                count: folioCount,
                            })}
                        </div>
                    </div>
                ) : !usesFolio || (usesBothCalculationMethods && !usesFolio) ? (
                    <PriceFormComponent
                        label={t("NewArticle.FixedPrice")}
                        placeholder={""}
                        control={control}
                        controlName="folioPrice"
                        disabled={!hasPermissions(["article:new"])}
                        error={errors.folioPrice}
                    />
                ) : (
                    <></>
                )}
            </div>
        </>
    );
};

export default PaymentStep;
